import { format, isValid, max } from 'date-fns';
import { useTranslate } from 'ra-core';
import { useWatch } from 'react-hook-form';
import { AutocompleteArrayInput } from '../../components/admin/autocomplete-array-input';
import { DateTimeInput } from '../../components/admin/date-time-input';
import { ReferenceArrayInput } from '../../components/admin/reference-array-input';
import { normalizeDateToISOString } from '../../utils/normalizeDateToISOString';
import {
  validateAssignDateTime,
  validateDueOn,
  validateSendReminderOn,
} from './nudgeAssignmentValidation';

const DATE_TIME_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

export const NudgeAssignmentInput = () => {
  const assignedFrom = useWatch({ name: 'assignedFrom' });
  const dueOn = useWatch({ name: 'dueOn' });
  const visibleTo = useWatch({ name: 'userGroupIds' });
  const translate = useTranslate();
  const now = new Date();
  // The assignment is only possible for groups the nudge is visible to
  const disabled = visibleTo?.length === 0;

  return (
    <section className="flex w-full flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold">
          {translate('moonstar.nudges.assignment.title')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {translate('moonstar.nudges.assignment.description')}
        </p>
      </div>
      <ReferenceArrayInput
        source="assigneeGroupIds"
        reference="user-groups"
        filter={{ id: visibleTo ?? [] }}
        perPage={999}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteArrayInput
          disabled={disabled}
          optionText="name"
          filterToQuery={(q: string) => ({ name: q })}
          helperText="moonstar.nudges.assignment.assigneeGroupIdsHelperText"
        />
      </ReferenceArrayInput>
      <DateTimeInput
        disabled={disabled}
        source="assignedFrom"
        helperText="moonstar.nudges.assignment.assignedFromHelperText"
        parse={normalizeDateToISOString}
        validate={validateAssignDateTime}
        min={format(
          // Add 1 minute to now to avoid validation error when the user selects the current time
          new Date(now.getTime() + 60_000),
          DATE_TIME_INPUT_FORMAT
        )}
      />
      <DateTimeInput
        disabled={disabled}
        source="dueOn"
        helperText="moonstar.nudges.assignment.dueOnHelperText"
        parse={normalizeDateToISOString}
        validate={validateDueOn}
        min={format(latestDate(now, assignedFrom), DATE_TIME_INPUT_FORMAT)}
      />
      <DateTimeInput
        disabled={disabled}
        source="sendReminderOn"
        helperText="moonstar.nudges.assignment.sendReminderOnHelperText"
        parse={normalizeDateToISOString}
        validate={validateSendReminderOn}
        min={format(latestDate(now, assignedFrom), DATE_TIME_INPUT_FORMAT)}
        max={
          dueOn ? format(new Date(dueOn), DATE_TIME_INPUT_FORMAT) : undefined
        }
      />
    </section>
  );
};

function latestDate(...values: (Date | string | null | undefined)[]) {
  return max(
    values
      .filter((value): value is Date | string => Boolean(value))
      .map((value) => new Date(value))
      .filter(isValid)
  );
}
